/**
 * Single source of truth for where a table's seats sit.
 *
 * This geometry used to be copy-pasted in three places — computeSeats() in
 * ChartDesigner (which feeds the SAVED layout), DesignerCanvas (what the admin
 * sees) and UnifiedSeatMap (what the customer sees). They drifted, so a table
 * could be drawn one way and sold another. Every caller now goes through here.
 *
 * Round tables ring the body. Rectangle tables run along the edges, either
 * evenly around the perimeter or with an explicit seat count per side.
 */

export interface TableSeatsPerSide {
  top: number
  right: number
  bottom: number
  left: number
}

export interface TableSeatLayoutInput {
  /** Section box in world units (the table body is inset within it). */
  x: number
  y: number
  width: number
  height: number
  shape: 'round' | 'rectangle'
  capacity: number
  /** Rectangle tables only. When set (and non-zero) it overrides even spacing. */
  seatsPerSide?: TableSeatsPerSide | null
  /** Explicit seat radius; derived from available room when omitted. */
  seatSize?: number | null
}

export interface TableSeatPoint {
  x: number
  y: number
  radius: number
  index: number
}

/** Gap between the drawn table body and the seat centres. */
export const TABLE_SEAT_INSET = 14
/** The body is drawn inset this far inside the section box. */
export const TABLE_BODY_INSET = 15

export function tableBodyRadius(width: number, height: number): number {
  return Math.min(width, height) / 2 - TABLE_BODY_INSET
}

/** Total seats implied by an explicit per-side distribution. */
export function seatsPerSideTotal(sps?: TableSeatsPerSide | null): number {
  if (!sps) return 0
  return (sps.top || 0) + (sps.right || 0) + (sps.bottom || 0) + (sps.left || 0)
}

/**
 * Spread `capacity` across four sides the way a real table seats people:
 * the long sides fill first, ends get one each. Used as the default when a
 * table has no explicit distribution yet.
 */
export function defaultSeatsPerSide(width: number, height: number, capacity: number): TableSeatsPerSide {
  const cap = Math.max(0, Math.floor(capacity))
  if (cap === 0) return { top: 0, right: 0, bottom: 0, left: 0 }
  if (cap <= 2) return { top: cap, right: 0, bottom: 0, left: 0 }

  const landscape = width >= height
  // Ends take one seat each when there are enough to go round.
  const ends = cap >= 4 ? 2 : 0
  const along = cap - ends
  const first = Math.ceil(along / 2)
  const second = along - first

  return landscape
    ? { top: first, bottom: second, left: ends ? 1 : 0, right: ends ? 1 : 0 }
    : { left: first, right: second, top: ends ? 1 : 0, bottom: ends ? 1 : 0 }
}

/** Walk the rectangle clockwise from the top-left corner. */
function pointOnRect(
  dist: number, cx: number, cy: number, rectW: number, rectH: number,
): [number, number] {
  const perimeter = 2 * (rectW + rectH)
  let d = ((dist % perimeter) + perimeter) % perimeter
  const left = cx - rectW / 2
  const top = cy - rectH / 2
  if (d < rectW) return [left + d, top]
  d -= rectW
  if (d < rectH) return [left + rectW, top + d]
  d -= rectH
  if (d < rectW) return [left + rectW - d, top + rectH]
  d -= rectW
  return [left, top + rectH - d]
}

/** Evenly spaced positions along one edge, inset from the corners. */
function alongEdge(
  count: number,
  from: [number, number],
  to: [number, number],
): Array<[number, number]> {
  const out: Array<[number, number]> = []
  if (count <= 0) return out
  for (let i = 0; i < count; i++) {
    // (i + 1) / (count + 1) keeps seats off the corners and centres the run.
    const t = (i + 1) / (count + 1)
    out.push([from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t])
  }
  return out
}

export function computeTableSeats(input: TableSeatLayoutInput): TableSeatPoint[] {
  const { x, y, width, height, shape, capacity } = input
  const cap = Math.max(0, Math.floor(capacity))
  if (cap === 0) return []

  const cx = x + width / 2
  const cy = y + height / 2
  const bodyR = tableBodyRadius(width, height)

  if (shape === 'round') {
    const ringR = Math.max(1, bodyR) + TABLE_SEAT_INSET
    const radius = input.seatSize ?? clampSeatRadius((Math.PI * 2 * ringR) / cap / 2 - 1)
    const out: TableSeatPoint[] = []
    for (let i = 0; i < cap; i++) {
      const angle = (i / cap) * Math.PI * 2 - Math.PI / 2
      out.push({ x: cx + ringR * Math.cos(angle), y: cy + ringR * Math.sin(angle), radius, index: i })
    }
    return out
  }

  // Rectangle: the seat ring sits TABLE_SEAT_INSET outside the drawn body.
  const rectW = Math.max(1, width - TABLE_BODY_INSET * 2) + TABLE_SEAT_INSET * 2
  const rectH = Math.max(1, height - TABLE_BODY_INSET * 2) + TABLE_SEAT_INSET * 2
  const perimeter = 2 * (rectW + rectH)

  const sps = input.seatsPerSide
  const perSideTotal = seatsPerSideTotal(sps)

  if (sps && perSideTotal > 0) {
    const left = cx - rectW / 2
    const right = cx + rectW / 2
    const top = cy - rectH / 2
    const bottom = cy + rectH / 2
    // Seat spacing differs per side, so size from the tightest run.
    const tightest = Math.min(
      sps.top ? rectW / (sps.top + 1) : Infinity,
      sps.bottom ? rectW / (sps.bottom + 1) : Infinity,
      sps.left ? rectH / (sps.left + 1) : Infinity,
      sps.right ? rectH / (sps.right + 1) : Infinity,
    )
    const radius = input.seatSize ?? clampSeatRadius(tightest / 2 - 1)
    const pts: Array<[number, number]> = [
      ...alongEdge(sps.top || 0, [left, top], [right, top]),
      ...alongEdge(sps.right || 0, [right, top], [right, bottom]),
      ...alongEdge(sps.bottom || 0, [right, bottom], [left, bottom]),
      ...alongEdge(sps.left || 0, [left, bottom], [left, top]),
    ]
    return pts.slice(0, cap).map(([px, py], i) => ({ x: px, y: py, radius, index: i }))
  }

  // No explicit distribution: spread evenly, starting at top-centre so seat 1
  // lands where it does on a round table.
  const radius = input.seatSize ?? clampSeatRadius(perimeter / cap / 2 - 1)
  const out: TableSeatPoint[] = []
  for (let i = 0; i < cap; i++) {
    const [px, py] = pointOnRect((i / cap) * perimeter + rectW / 2, cx, cy, rectW, rectH)
    out.push({ x: px, y: py, radius, index: i })
  }
  return out
}

function clampSeatRadius(r: number): number {
  return Math.min(8, Math.max(4, r))
}
